import { useState } from 'react';
import { useResolveAttachmentDecision } from '@/hooks/queries/useChainDecisions';
import { Button } from '@/components/ui/Button';

interface ChainLeg {
  optionType: 'put' | 'call' | string;
  strikePrice: number;
  quantity?: number;
  quantityRemaining?: number;
  action?: string;
  expirationDate: string | Date;
}

interface ChainPosition {
  legs: ChainLeg[];
}

interface ChainSummary {
  id: string;
  underlyingSymbol: string;
  strategyType?: string | null;
  positions: ChainPosition[];
}

interface Decision {
  id: string;
  underlyingSymbol: string;
  reason: 'multi_chain' | 'qty_over' | 'same_side_different_strikes';
  newChain: unknown;
  candidateChains: unknown[];
}

const reasonLabels: Record<Decision['reason'], string> = {
  multi_chain: 'Multiple open chains could match this order.',
  qty_over: 'This order is larger than the existing position on the other side.',
  same_side_different_strikes: 'This adds the same option type at different strikes.',
};

export function AttachmentDecisionModal({
  decision,
  onClose,
}: {
  decision: Decision;
  onClose: () => void;
}) {
  const candidateChains = (decision.candidateChains as ChainSummary[]) ?? [];
  const newChain = decision.newChain as ChainSummary | null;
  const [targetChainId, setTargetChainId] = useState<string | null>(
    candidateChains[0]?.id ?? null
  );
  const resolve = useResolveAttachmentDecision();

  async function handleMerge() {
    if (!targetChainId) return;
    await resolve.mutateAsync({ id: decision.id, action: 'merge', targetChainId });
    onClose();
  }

  async function handleKeep() {
    await resolve.mutateAsync({ id: decision.id, action: 'keep_separate' });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-2">
          Review {decision.underlyingSymbol} attachment
        </h2>
        <p className="text-sm text-gray-600 mb-4">{reasonLabels[decision.reason]}</p>

        <div className="space-y-3 mb-6">
          <div className="border rounded p-3">
            <div className="text-xs text-gray-500 mb-1">New order (currently its own chain)</div>
            <ChainSummaryView chain={newChain} />
          </div>

          <div className="text-xs text-gray-500 my-2">Candidate chains to merge into:</div>

          {candidateChains.map(c => (
            <label
              key={c.id}
              className="border rounded p-3 flex gap-3 cursor-pointer hover:bg-gray-50 has-[:checked]:bg-blue-50 has-[:checked]:border-blue-300"
            >
              <input
                type="radio"
                name="target"
                checked={targetChainId === c.id}
                onChange={() => setTargetChainId(c.id)}
                className="mt-1"
              />
              <div className="flex-1">
                <ChainSummaryView chain={c} />
              </div>
            </label>
          ))}
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>
            Decide later
          </Button>
          <Button variant="secondary" onClick={handleKeep} disabled={resolve.isPending}>
            Keep separate
          </Button>
          <Button
            variant="primary"
            onClick={handleMerge}
            disabled={!targetChainId || resolve.isPending}
          >
            Merge into selected
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChainSummaryView({ chain }: { chain: ChainSummary | null }) {
  if (!chain) return <div className="text-sm text-gray-400">Unknown chain</div>;
  const allLegs = chain.positions?.flatMap(p => p.legs) ?? [];
  return (
    <div className="text-sm">
      <div className="font-medium">
        {chain.underlyingSymbol} · {chain.strategyType ?? 'unknown'}
      </div>
      <div className="text-xs text-gray-500 font-mono mt-1">
        {allLegs.map((l, i) => {
          const qty = l.quantityRemaining ?? l.quantity ?? 0;
          const optionLabel = l.optionType === 'put' ? 'P' : 'C';
          const exp =
            typeof l.expirationDate === 'string'
              ? l.expirationDate.slice(5, 10)
              : new Date(l.expirationDate).toISOString().slice(5, 10);
          return (
            <div key={i}>
              {l.action ?? ''} {qty}x {l.strikePrice}
              {optionLabel} {exp}
            </div>
          );
        })}
      </div>
    </div>
  );
}
